<template>
  <div>
    <v-card class="mt-2" elevation="2">
      <v-toolbar color="primary" class="white--text" dense>
        <v-icon left color="white">mdi-account-details</v-icon>
        Test Details
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4"
            ><span class="font-weight-bold">DMV ID Number:</span>
            {{ results.CID }}</v-col
          >
          <v-col cols="12" md="4"
            ><span class="font-weight-bold">Road Test Date:</span>
            {{ new Date(results.TestDate).toLocaleDateString() }}</v-col
          >
          <v-col cols="12" md="4"
            ><span class="font-weight-bold">License Class:</span>
            {{ results.LicenseClass }}</v-col
          >
        </v-row>
        <v-row>
          <v-col cols="12" md="4"
            ><span class="font-weight-bold"
              >Result:<span :class="resultColor(results.OverallResult)">
                {{ results.OverallResult }}</span
              ></span
            >
          </v-col>
          <v-col cols="12" md="4"
            ><span class="font-weight-bold">Test Site Location:</span>
            {{ results.TestSiteName }}</v-col
          >
          <v-col cols="12" md="4"
            ><span class="font-weight-bold">Examiner ID:</span>
            {{ results.ExaminerNumber }}</v-col
          >
        </v-row>
        <v-row>
          <v-col>
            <div v-if="results.ScoredItems.length > 0">
              <div>
                Scored Items are items that were performed incorrectly or
                improperly. For each item, an applicant receives points. If an
                applicant receives more than
                {{ results.RoadTestScoreLimit }} points, they have failed the
                road test.
              </div>
              <span class="font-weight-bold"> Scored Items:</span>
              <br />
              <ul v-for="(item, index) in results.ScoredItems" :key="index">
                <ul>
                  <li>
                    {{ item.Group }}
                    <ul
                      v-for="(visualItem, visualIndex) in item.VisualItems"
                      :key="visualIndex"
                    >
                      <li>
                        {{ addColonIfMissing(visualItem.Description) }}
                        {{ visualItem.Points }}
                        <ul
                          v-for="(visualSubItem,
                          visualSubIndex) in visualItem.VisualSubItems"
                          :key="visualSubIndex"
                        >
                          <li>{{ visualSubItem }}</li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
              </ul>
            </div>
            <div v-if="results.IsCDLTest" class="font-weight-bold">
              Air Brake Result:
              <span class="font-weight-regular">{{
                results.AirBrakeResult
              }}</span>
            </div>
            <div v-if="results.IsCDLTest" class="font-weight-bold">
              Vehicle Inspection:
              <span class="font-weight-regular">{{
                results.VehicleInspectionResult
              }}</span>
            </div>
            <div v-if="results.IsCDLTest" class="font-weight-bold">
              Basic Control:
              <span class="font-weight-regular">{{
                results.BasicControlsResult
              }}</span>
            </div>
            <div v-if="results.IsCDLTest" class="font-weight-bold">
              Road:
              <span class="font-weight-regular">{{
                results.RoadTestResult
              }}</span>
            </div>
            <div v-if="automaticFailuresDisplay" class="font-weight-bold">
              Automatic Fail Category:
              <span class="font-weight-regular">{{
                automaticFailuresDisplay
              }}</span>
            </div>
            <div v-if="endorsementsDisplay" class="font-weight-bold">
              Endorsements:
              <span class="font-weight-regular">{{ endorsementsDisplay }}</span>
            </div>
            <div
              v-if="addRestrictionsDisplay || removeRestrictionsDisplay"
              class="font-weight-bold"
            >
              Restrictions:
            </div>
            <ul>
              <li v-if="addRestrictionsDisplay">
                Add: {{ addRestrictionsDisplay }}
              </li>
              <li v-if="removeRestrictionsDisplay">
                Remove: {{ removeRestrictionsDisplay }}
              </li>
            </ul>
            <div v-if="HoldsDisplay" class="font-weight-bold">
              Holds: <span class="font-weight-regular">{{ HoldsDisplay }}</span>
            </div>
            <div v-if="RedatesDisplay" class="font-weight-bold">
              Redates:
              <span class="font-weight-regular">{{ RedatesDisplay }}</span>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div v-if="results.LicenseClass == 'Class D'">
              See more
              <ExternalLink
                text="information about Class D road test scoring."
                url="https://www.dmv.ny.gov/roadtestscoring"
              />
            </div>
            <div v-if="results.LicenseClass == 'Class M'">
              See more
              <ExternalLink
                text="information about Class M road test scoring."
                url="https://www.dmv.ny.gov/roadtestscoring"
              />
            </div>
            <div v-if="results.IsCDLTest">
              See more
              <ExternalLink
                text="information about CDL road test scoring."
                url="https://www.dmv.ny.gov/roadtestscoring"
              />
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div
              v-if="
                !results.IsCDLTest &&
                  results.OverallResult == 'Pass' &&
                  results.LicenseClass == 'Class D'
              "
            >
              See information about what to do after you pass your test on the
              <ExternalLink
                text="schedule a road test webpage."
                url="https://dmv.ny.gov/schedule-road-test"
              />
            </div>
            <div
              v-if="
                !results.IsCDLTest &&
                  results.OverallResult == 'Fail' &&
                  results.LicenseClass == 'Class D'
              "
            >
              See information about what to if you fail your test on the
              <ExternalLink
                text="schedule a road test webpage."
                url="https://dmv.ny.gov/schedule-road-test"
              />
            </div>
            <div v-if="results.IsCDLTest && results.OverallResult == 'Pass'">
              Please see additional
              <ExternalLink
                text="information about getting a CDL."
                url="https://dmv.ny.gov/get-cdl"
              />
            </div>
            <div
              v-if="results.IsCDLTest && results.OverallResult == 'Fail'"
              class="font-weight-bold"
            >
              You need additional training. You do not meet the standards for
              licensure. You can reschedule your test online at:
              <ExternalLink
                text="https://dmv.ny.gov/schedule-road-test"
                url="https://dmv.ny.gov/schedule-road-test"
              />or by phone at (518) 402-2100.
            </div>
            <div
              v-if="
                !results.IsCDLTest &&
                  results.OverallResult == 'Pass' &&
                  results.LicenseClass == 'Class M'
              "
            >
              Please review steps to
              <ExternalLink
                text="Get Your Motorcycle License."
                url="https://dmv.ny.gov/driver-license/get-motorcycle-license"
              />
            </div>
            <div
              v-if="
                !results.IsCDLTest &&
                  results.OverallResult == 'Fail' &&
                  results.LicenseClass == 'Class M'
              "
            >
              See information about what to if you fail your test on the
              <ExternalLink
                text="schedule a road test webpage."
                url="https://dmv.ny.gov/schedule-road-test"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          v-if="!results.IsCDLTest && results.OverallResult == 'Pass'"
          color="primary"
          @click="showReceipt()"
        >
          <v-icon left>mdi-card-account-details</v-icon>
          View Temporary License
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import ExternalLink from '@/components/ExternalLink';
export default {
  name: 'TestResultDetails',
  components: {
    ExternalLink,
  },
  props: {
    results: {
      type: Object,
      required: true,
    },
  },

  computed: {
    automaticFailuresDisplay() {
      return this.results.AutomaticFailures.map((item) => {
        return `${item.Reason} (${item.Code})`;
      }).join(', ');
    },
    endorsementsDisplay() {
      return this.results.Endorsements.map((item) => {
        return `${item.Code} (${item.Description})`;
      }).join(', ');
    },
    addRestrictionsDisplay() {
      const filteredRestrictions = this.results.Restrictions.filter((item) => {
        return item.Remove === false;
      });

      return filteredRestrictions
        .map((e) => {
          return `${e.Code} (${e.Description})`;
        })
        .join(', ');
    },
    removeRestrictionsDisplay() {
      const filteredRestrictions = this.results.Restrictions.filter((item) => {
        return item.Remove === true;
      });

      return filteredRestrictions
        .map((e) => {
          return `${e.Code} (${e.Description})`;
        })
        .join(', ');
    },
    HoldsDisplay() {
      return this.results.Holds.map((item) => {
        return `${item.Reason} (${item.Code})`;
      }).join(', ');
    },
    RedatesDisplay() {
      return this.results.Redates.map((item) => {
        return `${item.Reason} (${item.Code})`;
      }).join(', ');
    },
  },

  methods: {
    showReceipt() {
      this.$emit('showReceipt');
    },
    addColonIfMissing(myString) {
      // Some visualItem.Description's have trailing colon's and some do not. This just makes it consistent.
      return myString[myString.length - 1] == ':' ? myString : myString + ':';
    },
    resultColor(result) {
      let color;
      switch (result) {
        case 'Pass':
          color = 'green--text text--darken-2';
          break;
        case 'Fail':
          color = 'red--text text--darken-2';
          break;
        default:
          color = 'blue--text text--darken-2';
      }

      return color;
    },
  },
};
</script>

<style scoped>
div {
  color: black;
}
</style>
